import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useFinanceAccess } from "@/features/finance/access";

/**
 * Gates finance-backed routes on the matching client predicate. The default
 * dashboard scope mirrors the AR/AP HUD cards.
 *
 * Mirrors AgencyRoute:
 *   - Loading shell while role grants are in flight.
 *   - Redirect to `/dashboard` when the user doesn't pass the gate.
 *
 * ProtectedRoute (composed outside this guard in App.tsx) handles the
 * unauthenticated case. RLS on public.finance_* enforces the real boundary
 * server-side; this is the UX gate.
 */
export function FinanceRoute({
  children,
}: {
  children: ReactNode;
}) {
  const dashboardAccess = useFinanceAccess();
  const { allowed, isLoading } = dashboardAccess;

  return (
    <FinanceAccessGate allowed={allowed} isLoading={isLoading}>
      {children}
    </FinanceAccessGate>
  );
}

function FinanceAccessGate({
  allowed,
  isLoading,
  children,
}: {
  allowed: boolean;
  isLoading: boolean;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
